import React, { createContext, useContext, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { ResourceType, isArchivedResourceOutput } from '@cotera/api';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import type { ArtifactDescriptor } from './artifacts';
import { shareableResourceForArtifact } from './artifacts/resource-artifact';

const EMPTY: ReadonlySet<string> = new Set();

/**
 * How long an archived verdict is served from cache before it is refetched.
 *
 * The same 60s `useArtifactTitle` uses, and deliberately the same query key —
 * every artifact on screen is already fetching its own resource to put a name
 * on the tab, so reading `status` off that answer costs nothing. Two different
 * stale times against one key would have the two hooks taking turns
 * invalidating each other's fetch.
 */
const ARCHIVED_STATUS_STALE_MS = 60_000;

type ArtifactResourceRef = {
  artifactId: string;
  type: ResourceType;
  id: string;
};

/**
 * The artifacts whose archived-ness is a question worth asking.
 *
 * Three kinds drop out. Charts, dashboards and workflow steps have no resource
 * row behind them, so there is nothing that could be archived. Folders have a
 * row but no `status` column and cannot be targeted by the archive action.
 * And a seeded draft — a new agent or workflow the chat is still creating —
 * does not exist server-side yet, so fetching it would write a NotFound into
 * the shared `resource.get` cache that `ChatArtifactAccessGate` then reads back
 * as "you don't have access to this".
 */
const archivableRefs = (
  artifacts: readonly ArtifactDescriptor[]
): ArtifactResourceRef[] => {
  const refs: ArtifactResourceRef[] = [];
  for (const artifact of artifacts) {
    const payload =
      typeof artifact.payload === 'object' && artifact.payload !== null
        ? (artifact.payload as { isNew?: boolean; isNewDraft?: boolean })
        : null;
    if (payload?.isNew === true || payload?.isNewDraft === true) {
      continue;
    }
    const resource = shareableResourceForArtifact(artifact);
    if (resource === null || resource.type === ResourceType.Folder) {
      continue;
    }
    refs.push({
      artifactId: artifact.artifactId,
      type: resource.type,
      id: resource.id,
    });
  }
  return refs;
};

const useArchivedArtifactIdsSubscription = (
  artifacts: readonly ArtifactDescriptor[]
): ReadonlySet<string> => {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();

  const refs = useMemo(() => archivableRefs(artifacts), [artifacts]);

  const results = useQueries({
    queries: refs.map((ref) => ({
      queryKey: queryKeys.resource.get({
        orgId,
        type: ref.type,
        id: ref.id,
      }),
      staleTime: ARCHIVED_STATUS_STALE_MS,
      queryFn: () => client.resource.get({ type: ref.type, id: ref.id }),
    })),
  });

  // `useQueries` hands back a fresh array every render and a dependency array
  // cannot change length, so the memo keys on the answer rather than the
  // results: the archived ids themselves, sorted into a stable string. A render
  // that changes nothing about which artifacts are archived therefore returns
  // the same `Set`, which is what keeps this out of the consumers' own memos.
  const archivedKey = refs
    .filter((_ref, index) => {
      const data = results[index]?.data;
      if (data === undefined || data.isErr() === true) {
        return false;
      }
      return isArchivedResourceOutput(data.value);
    })
    .map((ref) => ref.artifactId)
    .sort()
    .join('|');

  return useMemo(
    () => (archivedKey === '' ? EMPTY : new Set(archivedKey.split('|'))),
    [archivedKey]
  );
};

const ArchivedArtifactIdsContext = createContext<ReadonlySet<string> | null>(
  null
);

/**
 * Publishes, once for the whole panel, which of its artifacts have been
 * archived.
 *
 * Mounted by `ArtifactSidePanel` beside
 * {@link DirtyArtifactResourceIdsProvider} and for the same reason: the two
 * surfaces that list artifacts — the bar's switcher and the artifacts index
 * card — both need the answer, and the index card in particular renders `null`
 * whenever the Canvas is open, so a hook of its own would go cold exactly when
 * the switcher needed it.
 *
 * Archiving is not itself an event this subscribes to. It does not have to be:
 * `ArchiveResourceAction` drops the archived resource's `resource.get` entry
 * from the query cache, which is the key these queries are keyed by, so the
 * refetch that follows is what carries the new status back.
 */
export const ArchivedArtifactIdsProvider: React.FC<{
  artifacts: readonly ArtifactDescriptor[];
  children: React.ReactNode;
}> = ({ artifacts, children }) => {
  const archivedArtifactIds = useArchivedArtifactIdsSubscription(artifacts);
  return (
    <ArchivedArtifactIdsContext.Provider value={archivedArtifactIds}>
      {children}
    </ArchivedArtifactIdsContext.Provider>
  );
};

/**
 * Artifact ids whose backing resource has been archived — the ones the panel's
 * lists leave out.
 *
 * Only the lists. An archived artifact that is still the active tab keeps its
 * place: it is what the user is looking at, and vanishing it mid-gesture would
 * answer "where did my artifact go" by doing the thing that prompted the
 * question. `ChatArtifactAccessGate` puts an `ArchivedArtifactNotice` in its
 * body instead.
 *
 * Empty outside an {@link ArchivedArtifactIdsProvider}, matching the rest of
 * the panel's context hooks: a component rendered away from the panel lists
 * everything rather than throwing.
 */
export function useArchivedArtifactIds(): ReadonlySet<string> {
  return useContext(ArchivedArtifactIdsContext) ?? EMPTY;
}
